import { ChangeDetectionStrategy, Component, inject, OnInit, Signal } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { filter, map, switchMap, take, tap } from 'rxjs';
import { JoinToTeamCommandHandler } from '../../application/handlers';
import {
  AppRoutes,
  AUTH_CLIENT_TOKEN,
  AuthClientProvider,
  MODAL_TOKEN,
  ModalProvider,
  PLATFORM_TOKEN,
  PlatformProvider,
  PresentModalComponent,
  TeamsRoutes
} from '@core';
import { BlockedWebViewComponent } from '@ui-components';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-team-invitation-accept',
  templateUrl: 'team-invitation-accept.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [BlockedWebViewComponent],
  providers: [JoinToTeamCommandHandler]
})
export class TeamInvitationAcceptComponent implements OnInit {
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly joinToTeamCommandHandler: JoinToTeamCommandHandler = inject(JoinToTeamCommandHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly authClientProvider: AuthClientProvider = inject(AUTH_CLIENT_TOKEN);

  readonly isNative: boolean = this.platformProvider.isNativeRun;

  readonly redirectPath: Signal<string | undefined> = toSignal(
    this.activatedRoute.params.pipe(
      map(
        (params: Params) => `${AppRoutes.TEAMS}/${TeamsRoutes.ACCEPT_INVITATION}/${params['teamId']}/${params['token']}`
      )
    )
  );

  ngOnInit(): void {
    this.handleNativeInvitation();
  }

  private handleNativeInvitation(): void {
    if (!this.isNative) return;

    this.authClientProvider.isAuthed$
      .pipe(
        take(1),
        tap((isAuthed: boolean) => {
          if (!isAuthed) {
            this.router.navigateByUrl(AppRoutes.AUTH);
          }
        }),
        filter((isAuthed: boolean) => isAuthed),
        switchMap(() => this.activatedRoute.params),
        take(1),
        switchMap((params: Params) => {
          const teamId: string = params['teamId'];
          const token: string = params['token']?.includes('?') ? params['token'].split('?')[0] : params['token'];

          return this.joinToTeamCommandHandler.join(teamId, token);
        }),
        switchMap(() => this.router.navigateByUrl(AppRoutes.DASHBOARD))
      )
      .subscribe(() => this.showSuccessMessage());
  }

  private showSuccessMessage(): void {
    this.modalProvider.showModal$({
      component: PresentModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Zostałeś dodany do grupki!',
        message: '',
        btnTxt: 'Rozumiem, dzięki!'
      }
    });
  }
}
